import MapContainer from "@/components/map-container/MapContainer";
import MapButtonContainer from "@/components/map-container/components/map-button-container/MapButtonContainer";
import SliderObservation from "@/components/slider-observation/SliderObservation";
import StationMapButtons from "./station-map-buttons/StationMapButton";
import LayerMap from "./layer-map/LayerMap";

import { ZoomControl } from "@/components/map-container/components/ZoomControl";
import { SpeedControl } from "@/components/map-container/components/SpeedControl";
import { WEATHER_STORE } from "@/store/store";
import { WeatherSelect } from "@/store/type";
import { useSelect } from "@wordpress/data";
import { useMemo, useState } from "@wordpress/element";
import { useWindowSize } from "@/hooks/use-window-resize";
import { getCurrentDateWithDay } from "@/helpers/format-date";
import {
	fillMissingImagery,
	getFirstObsDateAndLast,
	mergeObservationDataWithStation,
} from "../helpers/observation-helper";
import { DataWeatherStation } from "@/schemas/observation-schema";

import "./style.scss";

export const App = () => {
	const width = useWindowSize();

	const { observations, imagery, stations, imageryFilter } = useSelect(
		(select: (store: typeof WEATHER_STORE) => WeatherSelect) => {
			const store = select(WEATHER_STORE);

			return {
				observations: store.getObservationStationState().stationData,
				imagery: store.getObservationImageryState().imageryData,
				stations: store.getCityState(),
				imageryFilter: store.getObservationFilterState(),
			};
		},
	);

	const [currentIndex, setCurrentIndex] = useState(0);
	const [speedValue, setSpeedValue] = useState(1);
	const [showStation, setShowStation] = useState(false);
	const [showImagery, setShowImagery] = useState(false);
	const [isImageryPlayed, setIsImageryPlayed] = useState(false);
	const [filter, setFilter] = useState<keyof DataWeatherStation | undefined>(
		"temperature",
	);

	const { firstDate } = useMemo(
		() => getFirstObsDateAndLast(observations),
		[observations],
	);

	const mergedStationWithObs = useMemo(
		() =>
			mergeObservationDataWithStation({
				firstDate,
				kuwaitStation: stations.cityData,
				observationData: observations,
			}),
		[firstDate, stations.cityData, observations],
	);

	const updatedImageryFile = useMemo(() => {
		return fillMissingImagery({
			imagery,
			observations: mergedStationWithObs,
		});
	}, [imagery, mergedStationWithObs]);

	const currentObservation = mergedStationWithObs[currentIndex];
	const currentDate = currentObservation?.[0]?.data?.date ?? firstDate;
	const { date, day } = getCurrentDateWithDay(currentDate);

	const handleNext = () => {
		setCurrentIndex((prev) =>
			Math.min(prev + 1, mergedStationWithObs.length - 1),
		);
	};

	const handlePrev = () => {
		setCurrentIndex((prev) => Math.max(prev - 1, 0));
	};

	const toggleImagePlay = (value: boolean) => {
		setIsImageryPlayed(value);
	};

	return (
		<div className="observation-map-container">
			<div className="observation-map-content">
				<LayerMap
					showStation={showStation}
					showImagery={showImagery}
					setShowStation={() => setShowStation((prev) => !prev)}
					setShowImagery={() => setShowImagery((prev) => !prev)}
				/>

				{showStation && (
					<StationMapButtons setFilter={(value) => setFilter(value)} />
				)}

				<MapContainer
					allowInteraction
					showRegionStroke={false}
					showStationLabelAndImage={false}
					filter={showStation ? filter : undefined}
					zoom={width > 720 ? 8 : 7.5}
					height={702}
					controls={[
						new ZoomControl(true),
						new SpeedControl({
							defaultSpeed: 1,
							onSpeedChange: setSpeedValue,
						}),
					]}
					imageryState={{
						showStation,
						show: showImagery,
						geotiff: updatedImageryFile,
						imageryFilter,
						speed: speedValue,
						isPlayed: isImageryPlayed,
						setImageryPlayer: (value: boolean) => toggleImagePlay(value),
						observationData: mergedStationWithObs,
						currentIndex,
						setCurrentIndexJson: setCurrentIndex,
					}}
				/>
			</div>

			<div className="observations-footer">
				<MapButtonContainer
					showButtonPause
					isPlaying={isImageryPlayed}
					handleClickPause={() => toggleImagePlay(!isImageryPlayed)}
					handleClickPrev={handlePrev}
					handleClickNext={handleNext}
					prevDisabled={currentIndex === 0}
					nextDisabled={currentIndex >= mergedStationWithObs.length - 1}
				/>

				<SliderObservation
					maxValue={observations.length - 1}
					currentValue={currentIndex}
				/>

				<div className="date-content">
					<p>{day}</p>
					<p>{date}</p>
					<span>{currentDate.getHours()}:00</span>
				</div>
			</div>
		</div>
	);
};

export default App;
